import Main from "niteroi/packages/sales-distribution/controllers/Main.controller";
import {SearchField$LiveChangeEvent} from "sap/m/SearchField";
import {ICreditNote, IInvoice} from "niteroi/packages/sales-distribution/models/interfaces";
import JSONModel from "sap/ui/model/json/JSONModel";
import {Route$PatternMatchedEvent} from "sap/ui/core/routing/Route";
import Event from "sap/ui/base/Event";
import Table, {Table$RowSelectionChangeEvent} from "sap/ui/table/Table";
import GenericTile from "sap/m/GenericTile";
import CustomData from "sap/ui/core/CustomData";
import ListBinding from "sap/ui/model/ListBinding";
import Filter from "sap/ui/model/Filter";
import FilterOperator from "sap/ui/model/FilterOperator";
import Sorter from "sap/ui/model/Sorter";
import Input, {Input$SuggestionItemSelectedEvent, Input$ValueHelpRequestEvent} from "sap/m/Input";
import TableSelectDialog, {
    TableSelectDialog$ConfirmEvent,
    TableSelectDialog$SearchEvent
} from "sap/m/TableSelectDialog";
import ColumnListItem from "sap/m/ColumnListItem";

export default class Credits extends Main {
    private readonly onPatternMatchedHandler = this.onPatternMatched.bind(this);
    private readonly updateAnalyticsHandler = this.updateAnalytics.bind(this);

    public onInit(): void {
        super.onInit();

        const creditsModel = this.getModel("salesDistributionCreditCollection");
        creditsModel?.detachRequestCompleted(this.updateAnalyticsHandler, this);
        creditsModel?.attachRequestCompleted(this.updateAnalyticsHandler, this);

        const router = this.getRouter();
        const routeName = this.getCurrentRouteName(router);
        if (routeName) {
            const route = router.getRoute(routeName);
            route.detachPatternMatched(this.onPatternMatchedHandler);
            route.attachPatternMatched(this.onPatternMatchedHandler);
        }
        this.updateAnalytics();
    }

    public onExit(): void {
        this.getModel("salesDistributionCreditCollection")?.detachRequestCompleted(this.updateAnalyticsHandler, this);
    }

    public onBeforeRendering(): void {
        super.onBeforeRendering();
        this.initializeDocumentModels();
        this.initializeAnalyticsModel();
    }

    protected onPatternMatched(event: Route$PatternMatchedEvent): void {
        this.getData();
        this.syncDocumentModelFromRoute(event);
        this.updateAnalytics();
    }

    protected getData(): void {
        this.loadCollectionData("credits", "salesDistributionCreditCollection");
    }

    public onListSearch(event: SearchField$LiveChangeEvent): void {
        this.onSearchList(event, "idCreditsTable", ["creditNoteNumber", "status", "creditReason", "currency", "netValue", "invoiceId", "createdAt", "updatedAt"]);
    }

    /* ---------------------------------------------------------------------- *
     *  Navigation & lifecycle
     * ---------------------------------------------------------------------- */

    public onNavButtonPress(): void {
        this.navTo("sales-distribution/credits", {}, false);
    }

    public onCreateCreditPress(): void {
        this.setDocumentModel(this.getBlankDocumentModel());
        this.navTo("sales-distribution/credits/add", {}, true);
    }

    public onRefreshCreditsPress(): void {
        this.getModel("salesDistributionCreditCollection")?.refresh(true);
        this.updateAnalytics();
    }

    public onRowSelectionChange(event: Table$RowSelectionChangeEvent): void {
        const rowContext = event.getParameter("rowContext");
        const selected = rowContext?.getObject() as ICreditNote | undefined;
        if (!selected?.creditNoteId) {
            return;
        }
        this.setDocumentModel(selected);
        this.navTo("sales-distribution/credits/edit", {id: selected.creditNoteId}, false);
    }

    private initializeDocumentModels(): void {
        if (!this.getModel("creditNote")) {
            this.setModel(new JSONModel(this.getBlankDocumentModel()), "creditNote");
        }
        if (!this.getModel("creditNoteInvoice")) {
            this.setModel(new JSONModel({}), "creditNoteInvoice");
        }
    }

    private initializeAnalyticsModel(): void {
        if (!this.getModel("salesDistributionCreditsAnalytics")) {
            this.setModel(new JSONModel(this.getDefaultAnalytics()), "salesDistributionCreditsAnalytics");
        }
    }

    private syncDocumentModelFromRoute(event: Route$PatternMatchedEvent): void {
        const routeName = event.getParameter("name");
        if (routeName === "sales-distribution/credits/add") {
            this.setDocumentModel(this.getBlankDocumentModel());
            return;
        }
        if (routeName !== "sales-distribution/credits/edit") {
            return;
        }
        const id = (event.getParameter("arguments") as { id?: string })?.id ?? "";
        if (!id) {
            return;
        }
        const credits = (this.getModel("salesDistributionCreditCollection")?.getData() as ICreditNote[]) ?? [];
        const selected = credits.find((credit) => credit.creditNoteId === id);
        if (selected) {
            this.setDocumentModel(selected);
        }
    }

    private setDocumentModel(document: ICreditNote): void {
        let model = this.getModel("creditNote");
        if (!model) {
            model = new JSONModel(this.getBlankDocumentModel());
            this.setModel(model, "creditNote");
        }
        model.setData({...document});
        this.resolveInvoice(document.invoiceId);
    }

    private resolveInvoice(invoiceId: string): void {
        let invoiceModel = this.getModel("creditNoteInvoice");
        if (!invoiceModel) {
            invoiceModel = new JSONModel({});
            this.setModel(invoiceModel, "creditNoteInvoice");
        }
        if (!invoiceId) {
            invoiceModel.setData({});
            return;
        }
        const invoices = (this.getModel("salesDistributionInvoiceCollection")?.getData() as IInvoice[]) ?? [];
        const invoice = invoices.find((entry) => entry.invoiceId === invoiceId);
        invoiceModel.setData(invoice ? {...invoice} : {});
    }

    /* ---------------------------------------------------------------------- *
     *  Persistence & lifecycle transitions
     * ---------------------------------------------------------------------- */

    public onPressSaveDraft(): void {
        this.getModel("creditNote").setProperty("/status", "OPEN");
        this.upsertCredit();
    }

    public onPressSaveDocument(): void {
        const model = this.getModel("creditNote");
        const credit = model.getData() as ICreditNote;

        if (!credit.invoiceId || !credit.creditReason || Number(credit.netValue) <= 0) {
            return;
        }
        model.setProperty("/status", "POSTED");
        this.upsertCredit();
    }

    public onPressMarkAsPaid(): void {
        this.getModel("creditNote").setProperty("/status", "PAID");
        this.upsertCredit();
    }

    public onPressCancelButton(): void {
        this.getModel("creditNote").setProperty("/status", "CANCELLED");
        this.upsertCredit();
    }

    public onPressViewDocument(): void {
    }

    private upsertCredit(): void {
        const model = this.getModel("creditNote");
        const credit = model.getData() as ICreditNote;
        const collection = this.getModel("salesDistributionCreditCollection");
        const credits = collection.getData() as ICreditNote[];
        const timestamp = new Date().toISOString().replace("T", " ").split(".")[0];

        if (!credit.creditNoteId) {
            credit.creditNoteId = this.generateId();
            credit.creditNoteNumber = credit.creditNoteNumber || this.generateCreditNoteNumber(credits);
            credit.createdAt = timestamp;
        }
        credit.updatedAt = timestamp;
        model.setData(credit);

        const existingIndex = credits.findIndex((entry) => entry.creditNoteId === credit.creditNoteId);
        if (existingIndex !== -1) {
            collection.setProperty(`/${existingIndex}`, {...credit});
        } else {
            credits.push({...credit});
        }
        collection.refresh(true);
        this.updateAnalytics();
    }

    /* ---------------------------------------------------------------------- *
     *  Reversal handling
     * ---------------------------------------------------------------------- */

    public onChangeReversalType(): void {
        const model = this.getModel("creditNote");
        const isPartial = model.getProperty("/isPartialReversal") as boolean;
        if (!isPartial) {
            // Full reversal mirrors the complete invoice net value.
            const invoiceNet = this.getModel("creditNoteInvoice")?.getProperty("/netValue") as string | undefined;
            if (invoiceNet) {
                model.setProperty("/netValue", invoiceNet);
            }
        }
    }

    /* ---------------------------------------------------------------------- *
     *  Invoice value help
     * ---------------------------------------------------------------------- */

    public onValueHelpRequest(event: Input$ValueHelpRequestEvent): void {
        const dialog = this.byId("invoiceValueHelpDialog") as TableSelectDialog;
        if (dialog) {
            dialog.open(event.getSource().getValue());
        }
    }

    public onSuggestionItemSelected(event: Input$SuggestionItemSelectedEvent): void {
        const row = event.getParameter("selectedRow") as ColumnListItem;
        if (!row) {
            return;
        }
        const context = row.getBindingContext("salesDistributionInvoiceCollection");
        if (context) {
            this._updateCreditInvoice(context.getObject() as IInvoice);
        }
    }

    public onValueHelpSearch(event: TableSelectDialog$SearchEvent): void {
        const value = event.getParameter("value") || "";
        const dialog = event.getSource() as TableSelectDialog;
        const binding = dialog.getBinding("items") as ListBinding;

        const creditableFilter = this.getCreditableInvoiceFilter();

        if (!value) {
            binding.filter([creditableFilter]);
            return;
        }

        const searchFilters = [
            new Filter("invoiceNumber", FilterOperator.Contains, value)
        ];
        const combined = new Filter({filters: searchFilters, and: false});
        const finalFilter = new Filter({filters: [creditableFilter, combined], and: true});
        binding.filter([finalFilter]);
    }

    public onValueHelpConfirm(event: TableSelectDialog$ConfirmEvent): void {
        const selectedItem = event.getParameter("selectedItem");
        if (selectedItem) {
            const context = selectedItem.getBindingContext("salesDistributionInvoiceCollection");
            if (context) {
                this._updateCreditInvoice(context.getObject() as IInvoice);
            }
        }
        this._resetValueHelpDialogFilters(event);
    }

    public onValueHelpCancel(event: Event): void {
        this._resetValueHelpDialogFilters(event);
    }

    public onPressChangeInvoice(): void {
        this.getModel("creditNoteInvoice").setData({});
        const creditModel = this.getModel("creditNote");
        creditModel.setProperty("/invoiceId", "");
        creditModel.setProperty("/currency", "EUR");
        (this.byId("invoiceInput") as Input).fireValueHelpRequest();
    }

    private _updateCreditInvoice(invoice: IInvoice): void {
        const creditModel = this.getView()?.getModel("creditNote") as JSONModel;
        if (!creditModel) {
            return;
        }
        creditModel.setProperty("/invoiceId", invoice.invoiceId);
        creditModel.setProperty("/currency", invoice.currency);
        // Default a full reversal amount; the user can switch to a partial figure.
        if (!creditModel.getProperty("/isPartialReversal")) {
            creditModel.setProperty("/netValue", invoice.netValue);
        }
        this.getModel("creditNoteInvoice").setData({...invoice});
    }

    protected _resetValueHelpDialogFilters(event: Event): void {
        const dialog = event.getSource() as TableSelectDialog;
        const binding = dialog.getBinding("items") as ListBinding;
        binding?.filter([this.getCreditableInvoiceFilter()]);
    }

    private getCreditableInvoiceFilter(): Filter {
        // Only POSTED or PAID invoices can be credited.
        return new Filter({
            filters: [
                new Filter("status", FilterOperator.EQ, "POSTED"),
                new Filter("status", FilterOperator.EQ, "PAID")
            ],
            and: false
        });
    }

    /* ---------------------------------------------------------------------- *
     *  Analytics dashboard
     * ---------------------------------------------------------------------- */

    public onAnalyticsTilePress(event: Event): void {
        const tile = event.getSource() as GenericTile;
        const filterData = (tile.getCustomData() as CustomData[]).find((data) => data.getKey() === "filterType");
        if (!filterData) {
            return;
        }
        const filterType = filterData.getValue() as string;
        const table = this.byId("idCreditsTable") as Table;
        const binding = table?.getBinding("rows") as ListBinding;
        if (!binding) {
            return;
        }

        const filters: Filter[] = [];
        const sorters: Sorter[] = [];

        switch (filterType) {
            case "OPEN":
                filters.push(new Filter("status", FilterOperator.EQ, "OPEN"));
                break;
            case "POSTED":
                filters.push(new Filter("status", FilterOperator.EQ, "POSTED"));
                sorters.push(new Sorter("netValue", true, false, (a: string, b: string) => Number(a) - Number(b)));
                break;
            case "PAID":
                filters.push(new Filter("status", FilterOperator.EQ, "PAID"));
                break;
            case "CANCELLED":
                filters.push(new Filter("status", FilterOperator.EQ, "CANCELLED"));
                break;
            case "PARTIAL":
                filters.push(new Filter("isPartialReversal", FilterOperator.EQ, true));
                break;
            case "TOTAL":
            default:
                sorters.push(new Sorter("createdAt", true));
                break;
        }

        binding.filter(filters);
        binding.sort(sorters);
    }

    private updateAnalytics(): void {
        const analyticsModel = this.getModel("salesDistributionCreditsAnalytics");
        if (!analyticsModel) {
            return;
        }
        const credits = (this.getModel("salesDistributionCreditCollection")?.getData() as ICreditNote[]) ?? [];
        if (!Array.isArray(credits) || credits.length === 0) {
            analyticsModel.setData(this.getDefaultAnalytics());
            return;
        }

        const totalCredits = credits.length;
        const draftCredits = credits.filter((credit) => credit.status === "OPEN").length;
        const postedCredits = credits.filter((credit) => credit.status === "POSTED").length;
        const paidCredits = credits.filter((credit) => credit.status === "PAID").length;
        const cancelledCredits = credits.filter((credit) => credit.status === "CANCELLED").length;
        const partialReversals = credits.filter((credit) => credit.isPartialReversal).length;

        const isLive = (credit: ICreditNote): boolean => credit.status !== "CANCELLED";
        const totalCreditedValue = credits.filter(isLive).reduce((sum, credit) => sum + Number(credit.netValue || 0), 0);
        const settledCreditedValue = credits
            .filter((credit) => credit.status === "PAID")
            .reduce((sum, credit) => sum + Number(credit.netValue || 0), 0);
        const outstandingCreditedValue = credits
            .filter((credit) => credit.status === "POSTED")
            .reduce((sum, credit) => sum + Number(credit.netValue || 0), 0);

        analyticsModel.setData({
            totalCredits,
            draftCredits,
            postedCredits,
            paidCredits,
            cancelledCredits,
            partialReversals,
            fullReversals: totalCredits - partialReversals,
            totalCreditedValue: Math.round(totalCreditedValue),
            settledCreditedValue: Math.round(settledCreditedValue),
            outstandingCreditedValue: Math.round(outstandingCreditedValue),
            settlementRatio: totalCreditedValue > 0 ? Math.round((settledCreditedValue / totalCreditedValue) * 100) : 0
        });
    }

    private getDefaultAnalytics(): object {
        return {
            totalCredits: 0,
            draftCredits: 0,
            postedCredits: 0,
            paidCredits: 0,
            cancelledCredits: 0,
            partialReversals: 0,
            fullReversals: 0,
            totalCreditedValue: 0,
            settledCreditedValue: 0,
            outstandingCreditedValue: 0,
            settlementRatio: 0
        };
    }

    /* ---------------------------------------------------------------------- *
     *  Helpers
     * ---------------------------------------------------------------------- */

    private generateId(): string {
        const cryptoRef = (globalThis as { crypto?: Crypto }).crypto;
        if (cryptoRef?.randomUUID) {
            return cryptoRef.randomUUID();
        }
        return `cn-${Date.now()}-${Math.floor(Math.random() * 1_000_000)}`;
    }

    private generateCreditNoteNumber(credits: ICreditNote[]): string {
        const highest = credits.reduce((max, credit) => {
            const value = Number(credit.creditNoteNumber);
            return Number.isFinite(value) && value > max ? value : max;
        }, 7100000000);
        return String(highest + 1);
    }

    private getBlankDocumentModel(): ICreditNote {
        return {
            creditNoteId: "",
            creditNoteNumber: "",
            status: "OPEN",
            creditReason: "",
            isPartialReversal: false,
            netValue: "0.00",
            currency: "EUR",
            createdAt: "",
            updatedAt: "",
            invoiceId: ""
        };
    }
}
